import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <h2 className="font-display text-2xl font-bold text-slate-900">Welcome back</h2>
      <p className="text-slate-500 text-sm mt-1 mb-7">Sign in to your client portal</p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-xs uppercase tracking-wider text-slate-500">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com" className="mt-1.5 h-11" data-testid="login-email-input" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-slate-500">Password</Label>
            <Link to="/forgot-password" className="text-xs font-medium text-orange-600 hover:text-orange-700"
              data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••" className="mt-1.5 h-11" data-testid="login-password-input" />
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit-btn"
          className="w-full h-11 text-white font-medium" style={{ background: "var(--cp-primary)" }}>
          {loading ? <Loader2 className="animate-spin" size={18} /> : <>Sign in <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </form>
    </AuthShell>
  );
}
